import {
  formatScore,
  isWeekly,
  plainLabel,
  type JourneyStage,
  type ScoreField,
  type ScoreMetric,
  type ScoreSection,
} from './scoreboard';
import { headline } from './scoreboardDashboard';

/** Pure layout logic for the Details cards: grouping, rates beside counts, per-item tables. */

export const stageTitles: Record<JourneyStage, string> = {
  reached: 'Reached',
  engaged: 'Engaged',
  hand: 'Raised a hand',
  call: 'Call booked',
};

export interface DetailRow {
  metric: ScoreMetric;
  /** Calculated rates that belong beside this count. */
  rates: ScoreMetric[];
}
export interface DetailGroup {
  /** Null when the section has no grouping to show. */
  title: string | null;
  rows: DetailRow[];
}
export interface ItemColumn {
  /** The metric name after the dot, without "(weekly)". */
  name: string;
  weekly: boolean;
}
export interface ItemCell<T> {
  field: T;
  /** Calculated rates of the same item that belong beside this number. */
  rates: T[];
}
export interface ItemTable<T = ScoreMetric> {
  columns: ItemColumn[];
  rows: { name: string; cells: Record<string, ItemCell<T> | undefined> }[];
}
export interface SectionDetails {
  groups: DetailGroup[];
  /** One table per set of items that track the same numbers (videos, pages...). */
  itemTables: ItemTable[];
  hasRates: boolean;
}

/** "Hero Vid CG1 · Plays" is the item "Hero Vid CG1" and the column "Plays". */
export function splitItemLabel(label: string): { item: string; metric: string } | null {
  const at = label.lastIndexOf(' · ');
  if (at < 1) return null;
  const item = label.slice(0, at).trim();
  const metric = label.slice(at + 3).trim();
  return item && metric ? { item, metric } : null;
}

function byPosition(a: ScoreMetric, b: ScoreMetric) {
  return a.position - b.position;
}

export function buildDetails(section: ScoreSection): SectionDetails {
  const visible = section.metrics.filter((m) => m.is_active).sort(byPosition);
  // LinkedIn ads keeps its own By ad table, so its fields all stay in the main list.
  const splitItems = section.slug !== 'linkedin-ads';
  const itemFields = splitItems ? visible.filter((m) => splitItemLabel(m.label)) : [];
  const main = visible.filter((m) => !itemFields.includes(m));
  const raw = main.filter((m) => m.value_type !== 'rate');
  const rates = main.filter((m) => m.value_type === 'rate');

  const rows = new Map<string, DetailRow>(raw.map((m) => [m.id, { metric: m, rates: [] }]));
  const standalone: DetailRow[] = [];
  for (const rate of rates) {
    // A percent sits beside the count on top, money (cost per x) beside the count underneath.
    const anchorId = rate.rate_format === 'money' ? rate.denominator_id : rate.numerator_id;
    const anchor = anchorId ? rows.get(anchorId) : undefined;
    if (anchor) anchor.rates.push(rate);
    else standalone.push({ metric: rate, rates: [] });
  }

  const ordered = [...rows.values()];
  const groups: DetailGroup[] = [];
  if (!ordered.some((r) => r.metric.journey_stage)) {
    groups.push({ title: null, rows: ordered });
  } else {
    let stage: JourneyStage | null = null;
    let current: DetailGroup | null = null;
    for (const row of ordered) {
      const next: JourneyStage | null = row.metric.journey_stage ?? stage;
      if (!current || next !== stage) {
        stage = next;
        current = { title: stage ? stageTitles[stage] : 'Numbers', rows: [] };
        groups.push(current);
      }
      current.rows.push(row);
    }
  }
  if (standalone.length) {
    const last = groups[groups.length - 1];
    if (last) last.rows.push(...standalone);
    else groups.push({ title: null, rows: standalone });
  }

  const itemTables = groupItems(itemFields);

  return {
    groups,
    itemTables,
    hasRates: groups.some((g) => g.rows.some((r) => r.rates.length > 0)),
  };
}

/**
 * "Item · Metric" fields as tables: one row per item, one column per metric. Items of the same
 * kind (videos, landing pages, other) share one table even when some lack a column, so a new
 * video never splits off into its own table. Daily columns come first, weekly ones last. A
 * calculated rate of an item sits beside its count (percent: numerator, money: denominator).
 */
export function groupItems<T extends ScoreField>(fields: T[]): ItemTable<T>[] {
  const byItem = new Map<string, T[]>();
  for (const field of fields) {
    const parts = splitItemLabel(field.label);
    if (!parts) continue;
    byItem.set(parts.item, [...(byItem.get(parts.item) ?? []), field]);
  }
  const tables = new Map<string, ItemTable<T>>();
  for (const [name, list] of byItem) {
    const raw = list.filter((f) => f.value_type !== 'rate');
    const rates = list.filter((f) => f.value_type === 'rate');
    const cells: Record<string, ItemCell<T> | undefined> = {};
    const columns: ItemColumn[] = [];
    const add = (field: T) => {
      const metric = splitItemLabel(field.label)?.metric ?? field.label;
      const key = plainLabel(metric).trim();
      if (!cells[key]) columns.push({ name: key, weekly: isWeekly(metric) });
      cells[key] = { field, rates: [] };
    };
    raw.forEach(add);
    for (const rate of rates) {
      const anchorId = rate.rate_format === 'money' ? rate.denominator_id : rate.numerator_id;
      const anchor = Object.values(cells).find((cell) => cell?.field.id === anchorId);
      if (anchor) anchor.rates.push(rate);
      else add(rate);
    }
    const kind = itemTableNames(columns).title;
    const table = tables.get(kind) ?? { columns: [], rows: [] };
    for (const column of columns) {
      const known = table.columns.find((c) => c.name === column.name);
      if (!known) table.columns.push({ ...column });
      else if (column.weekly) known.weekly = true;
    }
    table.rows.push({ name, cells });
    tables.set(kind, table);
  }
  return [...tables.values()].map((table) => ({
    ...table,
    columns: [...table.columns.filter((c) => !c.weekly), ...table.columns.filter((c) => c.weekly)],
  }));
}

/** What a table of items is: videos, landing pages, or plain items. Names its title and first column. */
export function itemTableNames(columns: ItemColumn[]): { title: string; head: string } {
  const names = columns.map((c) => c.name.toLowerCase());
  if (names.some((n) => n === 'plays' || n.startsWith('watched')))
    return { title: 'Videos', head: 'Video' };
  if (names.length && names.every((n) => n.includes('session') || n.includes('visit')))
    return { title: 'Landing pages', head: 'Page' };
  return { title: 'By item', head: 'Item' };
}

/** One line shown on a closed card: the headline and the next few counts. */
export function collapsedSummary(section: ScoreSection): string | null {
  const { groups } = buildDetails(section);
  const top = headline(section);
  const counts = groups
    .flatMap((g) => g.rows.map((r) => r.metric))
    .filter((m) => m.value_type !== 'rate');
  const picked = [...(top && counts.includes(top) ? [top] : []), ...counts.filter((m) => m !== top)]
    .slice(0, 4)
    .map((m) => {
      const cell = section.kind === 'snapshot' ? m.totals.selected : m.totals.seven;
      return `${m.label} ${formatScore(cell?.value, m)}`;
    });
  if (!picked.length) return null;
  return `${section.kind === 'snapshot' ? 'Now' : 'Last 7 days'}: ${picked.join(' · ')}`;
}
